import React from 'react';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { forNamespace } from 'app/common/lib/util/i18n';
import Icon, { IconColor, IconName } from 'app/common/components/controls/icon';
import { SocialShareLinks } from 'app/src/components/SocialShareLinks';
import styles from './ShareTemplatePopover.less';
import { LinkCopy } from 'app/src/components/LinkCopy';
import { Analytics, Source } from '@trello/atlassian-analytics';

const format = forNamespace('share template popover', {
  shouldEscapeStrings: false,
});

const goldFormat = forNamespace('products');

const TemplatePopover: React.FunctionComponent<{
  boardId: string;
  linkOnClick: () => void;
  teamId: string;
  url: string;
  visibility: string;
  name: string;
  visibilityText?: string;
}> = ({
  boardId,
  linkOnClick,
  teamId,
  url,
  visibility,
  name,
  visibilityText,
}) => {
  let icon;
  if (visibility === 'public') {
    icon = (
      <Icon
        className={styles.visibilityIcon}
        name={IconName.Public}
        color={IconColor.Green}
      />
    );
  } else {
    icon = (
      <Icon
        className={styles.visibilityIcon}
        name={IconName.OrganizationVisible}
        color={IconColor.Yellow}
      />
    );
  }

  return (
    <>
      <p>{format('share a view only link with your team')}</p>
      <LinkCopy
        url={url}
        eventCategory={Noun.SHARE_TEMPLATE_MENU}
        boardId={boardId}
        teamId={teamId}
      />
      <SocialShareLinks
        url={url}
        tweet={format('share template tweet', { templateName: name })}
        emailBody={format('share template email', {
          templateName: name,
        })}
        analyticsSource={Source.shareTemplateInlineDialog}
        hashtags={['trellotemplates']}
        boardId={boardId}
      />
      {visibilityText && (
        <>
          <hr />
          <div>
            {icon}
            <span className={styles.visibilityText}>{visibilityText}</span>
          </div>
        </>
      )}
      <hr />
      <a
        href="https://help.trello.com/article/1187-creating-template-boards"
        className={styles.learnMoreLink}
        onClick={linkOnClick}
        rel="noopener noreferrer"
        target="_blank"
        title={format('learn more about templates')}
      >
        {format('learn more about templates')}
      </a>
    </>
  );
};

const TemplateGoldPopover: React.FunctionComponent<{
  url: string;
  boardId: string;
  linkOnClick: () => void;
  teamId: string;
  name: string;
}> = ({ boardId, teamId, url, linkOnClick, name }) => (
  <>
    <div className={styles.imgContainer}>
      <img
        src={require('resources/images/template-share-gold.svg')}
        alt={goldFormat('gold')}
        role="presentation"
      />
    </div>
    <p>
      {format('share this template with a friend', {
        goldLink: (
          <a
            href={'/gold'}
            rel="noopener noreferrer"
            target="_blank"
            title={goldFormat('gold')}
            key="gold-link"
            onClick={linkOnClick}
          >
            {goldFormat('gold')}
          </a>
        ),
      })}
    </p>

    <LinkCopy
      url={url}
      eventCategory={Noun.SHARE_TEMPLATE_MENU}
      boardId={boardId}
      teamId={teamId}
      isTemplate
    />
    <SocialShareLinks
      url={url}
      tweet={format('share template tweet', { templateName: name })}
      emailBody={format('share template email', {
        templateName: name,
      })}
      analyticsSource={Source.shareTemplateInlineDialog}
      hashtags={['trellotemplates']}
      boardId={boardId}
    />
    <hr />
    <div>
      <Icon
        className={styles.visibilityIcon}
        name={IconName.Public}
        color={IconColor.Green}
      />
      <span className={styles.visibilityText}>
        {format('anyone on the internet can see this template')}
      </span>
    </div>
  </>
);

export const ShareTemplatePopover: React.FunctionComponent<{
  name?: string;
  orgName?: string;
  enterpriseName?: string;
  url?: string;
  storyPageUrl?: string;
  visibility?: string;
  boardId?: string;
  teamId?: string;
  username?: string;
}> = ({
  name = '',
  orgName,
  enterpriseName,
  url = '',
  storyPageUrl,
  visibility = '',
  boardId = '',
  teamId = '',
  username,
}) => {
  if (visibility === 'public' && username) {
    return (
      <div className={styles.popoverContent}>
        <TemplateGoldPopover
          name={name}
          url={`${storyPageUrl || url}/${username}/recommend`}
          boardId={boardId}
          teamId={teamId}
          linkOnClick={() => {
            trackUe({
              category: Noun.SHARE_TEMPLATE_MENU,
              verb: Verb.OPENS,
              directObj: Noun.GOLD_LINK,
              context: {
                boardId: boardId,
                teamId: teamId,
              },
            });
            Analytics.sendUIEvent({
              action: 'clicked',
              actionSubject: 'link',
              actionSubjectId: 'goldLink',
              source: 'shareTemplateInlineDialog',
              containers: {
                board: {
                  id: boardId,
                },
              },
            });
          }}
        />
      </div>
    );
  }

  let visibilityText;
  // not handling private visiblity because we don't show popover on private templates
  if (enterpriseName && visibility === 'enterprise') {
    visibilityText = format('all members of org can see this template', {
      orgName: enterpriseName,
    });
  } else if (orgName && visibility === 'org') {
    visibilityText = format('all members of org can see this template', {
      orgName,
    });
  } else if (visibility === 'public') {
    visibilityText = format('anyone on the internet can see this template');
  }

  return (
    <div className={styles.popoverContent}>
      <TemplatePopover
        url={storyPageUrl || url}
        boardId={boardId}
        teamId={teamId}
        visibility={visibility}
        visibilityText={visibilityText}
        name={name}
        linkOnClick={() => {
          trackUe({
            category: Noun.SHARE_TEMPLATE_MENU,
            verb: Verb.OPENS,
            directObj: Noun.HELP_DOC_LINK,
            context: {
              boardId: boardId,
              teamId: teamId,
            },
          });
          Analytics.sendUIEvent({
            action: 'clicked',
            actionSubject: 'link',
            actionSubjectId: 'templatesHelpLink',
            source: 'shareTemplateInlineDialog',
            containers: {
              board: {
                id: boardId,
              },
            },
          });
        }}
      />
    </div>
  );
};
